import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { SpreadsheetRow } from './SpreadsheetRow';
import { PlusIcon, RefreshCwIcon, SaveIcon } from 'lucide-react';
interface MonthData {
  id: number;
  month: string;
  year: number;
  gbpValue: string;
  exchangeRate: number | null;
  rateDate: string;
}
export const CurrencyConverter: React.FC = () => {
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const getCurrentMonthData = (): MonthData[] => {
    const startDate = new Date(2025, 4, 1); // May 2025 (month is 0-indexed)
    const months = [];
    for (let i = 0; i < 24; i++) {
      const date = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
      months.push({
        id: i + 1,
        month: date.toLocaleString('default', {
          month: 'long'
        }),
        year: date.getFullYear(),
        gbpValue: '',
        exchangeRate: null,
        rateDate: ''
      });
    }
    return months;
  };
  const [monthlyData, setMonthlyData] = useState<MonthData[]>(() => {
    // Load saved values from localStorage on initial mount
    const savedData = localStorage.getItem('currencyConverterData');
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (err) {
        console.error('Error loading saved data:', err);
      }
    }
    return getCurrentMonthData();
  });
  const fetchExchangeRateForDate = async (year: number, month: string): Promise<{
    rate: number;
    date: string;
  } | null> => {
    try {
      const monthIndex = new Date(Date.parse(month + ' 1, 2000')).getMonth();
      const targetDate = new Date(year, monthIndex, 5);
      const today = new Date();
      const dateToFetch = targetDate > today ? today : targetDate;
      const dateString = dateToFetch.toISOString().split('T')[0];
      const response = await axios.get(`https://api.frankfurter.app/${dateString}?from=GBP&to=AUD`);
      if (response.data && response.data.rates && response.data.rates.AUD) {
        return {
          rate: response.data.rates.AUD,
          date: response.data.date
        };
      }
      return null;
    } catch (err) {
      console.error(`Error fetching rate for ${month} ${year}:`, err);
      return null;
    }
  };
  const fetchAllExchangeRates = async () => {
    setLoading(true);
    setError(null);
    try {
      const updatedData = await Promise.all(monthlyData.map(async data => {
        const rateData = await fetchExchangeRateForDate(data.year, data.month);
        return {
          ...data,
          // Preserve the existing GBP value
          gbpValue: data.gbpValue,
          exchangeRate: rateData?.rate || null,
          rateDate: rateData?.date || ''
        };
      }));
      setMonthlyData(updatedData);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      setError('Failed to fetch exchange rates. Please try again later.');
      console.error('Error fetching exchange rates:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAllExchangeRates();
  }, []);
  const handleValueChange = (id: number, value: string) => {
    setMonthlyData(prevData => prevData.map(data => data.id === id ? {
      ...data,
      gbpValue: value
    } : data));
  };
  const handleSaveValues = () => {
    try {
      localStorage.setItem('currencyConverterData', JSON.stringify(monthlyData));
      setSaveMessage('Values saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      setSaveMessage('Error saving values. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };
  const addNewMonth = async () => {
    const lastMonth = monthlyData[monthlyData.length - 1];
    const lastDate = new Date(lastMonth.year, new Date(Date.parse(lastMonth.month + ' 1, 2000')).getMonth() + 1, 1);
    const newId = Math.max(...monthlyData.map(d => d.id)) + 1;
    const newMonth = lastDate.toLocaleString('default', {
      month: 'long'
    });
    const newYear = lastDate.getFullYear();
    const rateData = await fetchExchangeRateForDate(newYear, newMonth);
    setMonthlyData([...monthlyData, {
      id: newId,
      month: newMonth,
      year: newYear,
      gbpValue: '',
      exchangeRate: rateData?.rate || null,
      rateDate: rateData?.date || ''
    }]);
  };
  const handleRefresh = () => {
    fetchAllExchangeRates();
  };
  const calculateRunningTotal = (upToIndex: number): number => {
    return monthlyData.slice(0, upToIndex + 1).reduce((sum, data) => {
      const gbpValue = parseFloat(data.gbpValue);
      if (isNaN(gbpValue) || !data.exchangeRate) return sum;
      return sum + gbpValue * data.exchangeRate;
    }, 0);
  };
  const calculateRunningTotalGBP = (upToIndex: number): number => {
    return monthlyData.slice(0, upToIndex + 1).reduce((sum, data) => {
      const gbpValue = parseFloat(data.gbpValue);
      if (isNaN(gbpValue)) return sum;
      return sum + gbpValue;
    }, 0);
  };
  const calculateRemainingFromTotal = (upToIndex: number): number => {
    const targetTotal = 225000;
    const currentTotal = calculateRunningTotalGBP(upToIndex);
    return targetTotal - currentTotal;
  };
  const calculateTotal = (): {
    gbp: number;
    aud: number;
    remaining: number;
  } => {
    const totalGbp = monthlyData.reduce((sum, data) => {
      const value = parseFloat(data.gbpValue);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
    const totalAud = monthlyData.reduce((sum, data) => {
      const gbpValue = parseFloat(data.gbpValue);
      if (isNaN(gbpValue) || !data.exchangeRate) return sum;
      return sum + gbpValue * data.exchangeRate;
    }, 0);
    return {
      gbp: totalGbp,
      aud: totalAud,
      remaining: 225000 - totalGbp
    };
  };
  const totals = calculateTotal();
  return <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-19">
      <div className="p-4 bg-gray-100 border-b flex justify-between items-center" data-id="element-20">
        <div data-id="element-21">
          <h2 className="text-xl font-semibold" data-id="element-22">Monthly GBP to AUD Tracker</h2>
          <p className="text-sm text-gray-600" data-id="element-23">
            May 2025 - April 2027 (24 months) • Target: £225,000
          </p>
        </div>
        <div className="flex items-center" data-id="element-24">
          {lastUpdated && <span className="text-sm text-gray-500 mr-3" data-id="element-25">
              Last updated: {lastUpdated}
            </span>}
          <button onClick={handleRefresh} className="p-2 rounded hover:bg-gray-200 transition-colors" disabled={loading} title="Refresh exchange rates" data-id="element-26">
            <RefreshCwIcon size={20} className={`${loading ? 'animate-spin' : ''}`} data-id="element-27" />
          </button>
        </div>
      </div>
      {error && <div className="bg-red-50 text-red-600 p-3 border-b border-red-100" data-id="element-28">
          {error}
        </div>}
      <div className="overflow-x-auto" data-id="element-29">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-30">
          <thead className="bg-gray-50" data-id="element-31">
            <tr data-id="element-32">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-33">
                Month
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-34">
                Year
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-35">
                Amount (GBP £)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-36">
                Exchange Rate (5th)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-37">
                Result (AUD $)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-38">
                Running Total (GBP £)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-39">
                Running Total (AUD $)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-40">
                Remaining (GBP £)
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-41">
            {monthlyData.map((data, index) => <SpreadsheetRow key={data.id} rowId={data.id} month={data.month} year={data.year} gbpValue={data.gbpValue} exchangeRate={data.exchangeRate} rateDate={data.rateDate} runningTotalGBP={calculateRunningTotalGBP(index)} runningTotal={calculateRunningTotal(index)} remainingFromTotal={calculateRemainingFromTotal(index)} onValueChange={handleValueChange} loading={loading} data-id="element-42" />)}
            <tr className="bg-blue-50 font-semibold" data-id="element-43">
              <td className="px-6 py-4 whitespace-nowrap text-sm" colSpan={2} data-id="element-44">
                Total
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-45">
                <div className="flex items-center text-sm" data-id="element-46">
                  <span className="text-gray-500 mr-2" data-id="element-47">£</span>
                  {totals.gbp.toFixed(2)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-48">
                -
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-49">
                <div className="flex items-center text-sm" data-id="element-50">
                  <span className="text-gray-500 mr-2" data-id="element-51">$</span>
                  {totals.aud.toFixed(2)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-52">
                <div className="flex items-center text-sm" data-id="element-53">
                  <span className="text-gray-500 mr-2" data-id="element-54">£</span>
                  {totals.gbp.toFixed(2)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-55">
                <div className="flex items-center text-sm" data-id="element-56">
                  <span className="text-gray-500 mr-2" data-id="element-57">$</span>
                  {totals.aud.toFixed(2)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-58">
                <div className="flex items-center text-sm" data-id="element-59">
                  <span className="text-gray-500 mr-2" data-id="element-60">£</span>
                  <span className={totals.remaining < 0 ? 'text-red-600' : 'text-green-600'} data-id="element-61">
                    {totals.remaining.toFixed(2)}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t flex justify-between items-center" data-id="element-62">
        <button onClick={addNewMonth} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center" data-id="element-63">
          <PlusIcon size={18} className="mr-2" data-id="element-64" />
          Add Next Month
        </button>
        <div className="flex items-center gap-3" data-id="element-65">
          {saveMessage && <span className={`text-sm ${saveMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`} data-id="element-66">
              {saveMessage}
            </span>}
          <button onClick={handleSaveValues} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center" data-id="element-67">
            <SaveIcon size={18} className="mr-2" data-id="element-68" />
            Save Values
          </button>
        </div>
      </div>
    </div>;
};